import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, ArrowLeft, ExternalLink, Ticket, Plus, User, Building2, Network, Link2, Copy, RefreshCw, QrCode } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import TicketCard from "@/components/TicketCard";
import TicketDialog from "@/components/TicketDialog";
import { sortTickets } from "@/lib/tickets";

const telHref = (v) => `tel:${String(v).replace(/[^+\d]/g, "")}`;
const STATUS_DOT = { online: "bg-green-400", tv_reachable: "bg-amber-400", offline: "bg-red-400", no_agent: "bg-gray-500" };

export default function LocationDetail() {
  const { locationId } = useParams();
  const { lang } = useLang();
  const de = lang === "de";
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [net, setNet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, tr, ur, nr] = await Promise.all([
        api.get("/devices"),
        api.get("/tickets", { params: { location_id: locationId } }),
        api.get("/users").catch(() => ({ data: [] })),
        api.get(`/locations/${encodeURIComponent(locationId)}`).catch(() => ({ data: null })),
      ]);
      setDevices((dr.data || []).filter((d) => (d.location_id || "") === locationId));
      setTickets(tr.data || []);
      setUsers(ur.data || []);
      setNet(nr.data || null);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, [locationId]);
  useEffect(() => { load(); }, [load]);

  const sx = (k) => { for (const d of devices) { const v = String((d.extra || {})[k] || "").trim(); if (v) return v; } return ""; };
  const name = devices.map((d) => d.location_name).find(Boolean) || sx("Stationsname") || sx("Ort");
  const address = [sx("Straße"), [sx("PLZ"), sx("Ort")].filter(Boolean).join(" ")].filter(Boolean).join(", ") || sx("Adresse");
  const phone = sx("Telefon"), telIntern = sx("Tel intern"), email = sx("E-Mail Team");
  const manager = sx("LocManager"), agentur = sx("Agentur"), kst = sx("KST");
  const hours = [["Mo–Fr", sx("MoFr")], ["Sa", sx("Sa")], ["So", sx("So")]].filter(([, v]) => v);

  const sorted = useMemo(() => tickets.slice().sort(sortTickets), [tickets]);
  const openT = sorted.filter((t) => t.status !== "closed");
  const closedT = sorted.filter((t) => t.status === "closed");
  const devicesByLoc = useMemo(() => ({ [locationId]: [...devices].sort((a, b) => (a.device_code || "").localeCompare(b.device_code || "")) }), [devices, locationId]);

  const openEdit = (t) => { setEditing(t); setDlgOpen(true); };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <button data-testid="loc-back" onClick={() => navigate("/locations")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {de ? "Zurück zu Standorten" : "Back to locations"}
        </button>

        <div className="flex items-center gap-3 mb-1">
          <MapPin className="w-6 h-6 text-[#c00000]" />
          <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="loc-detail-code">{locationId}</h1>
          {openT.length > 0 && (
            <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border text-amber-700 bg-amber-500/15 border-amber-500/30">
              <Ticket className="w-3.5 h-3.5" /> {openT.length} {de ? "offen" : "open"}
            </span>
          )}
        </div>
        <p className="text-base text-gray-600 mb-6">{name || "—"}</p>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-gray-50 border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80 mb-4"><Building2 className="w-4 h-4" /> {de ? "Stationsinformationen" : "Station info"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {address && <Info label={de ? "Adresse" : "Address"} value={address} />}
              {manager && <Info label={de ? "Stationsleiter" : "Manager"} value={manager} icon={User} />}
              {phone && <InfoLink testid="loc-detail-phone" label={de ? "Telefon" : "Phone"} value={phone} href={telHref(phone)} icon={Phone} />}
              {telIntern && <InfoLink testid="loc-detail-phone-int" label={de ? "Tel. intern" : "Phone (int.)"} value={telIntern} href={telHref(telIntern)} icon={Phone} />}
              {email && <InfoLink testid="loc-detail-email" label="E-Mail" value={email} href={`mailto:${email}`} icon={Mail} />}
              {agentur && <Info label={de ? "Betriebsart" : "Type"} value={agentur} />}
              {kst && <Info label={de ? "Kostenstelle" : "Cost center"} value={kst} />}
              {hours.length > 0 && (
                <div className="sm:col-span-2">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5">{de ? "Öffnungszeiten" : "Opening hours"}</div>
                  <div className="flex flex-wrap gap-4">
                    {hours.map(([k, v]) => <div key={k} className="text-gray-700"><span className="text-gray-600">{k}:</span> {v}</div>)}
                  </div>
                </div>
              )}
            </div>

            {net && (net.net_online || net.net_vlan_ok || net.net_switch || net.net_port || net.net_id_checker || net.net_it_comment || net.net_sn_pc || net.net_sn_sc || net.net_tsr_remarks) && (
              <div className="mt-6 pt-5 border-t border-gray-200" data-testid="loc-network-block">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80 mb-3"><Network className="w-4 h-4" /> {de ? "Netzwerk / IT" : "Network / IT"}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {net.net_online && <NetBool label="Online" value={net.net_online} />}
                  {net.net_vlan_ok && <NetBool label={de ? "VLAN korrekt" : "VLAN OK"} value={net.net_vlan_ok} />}
                  {net.net_id_checker && <Info label={de ? "ID-Checker" : "ID checkers"} value={net.net_id_checker} />}
                  {net.net_switch && <Info label="Switch" value={net.net_switch} />}
                  {net.net_port && <Info label="Port" value={net.net_port} />}
                  {net.net_sn_pc && <Info label={de ? "SN Tablet" : "SN tablet"} value={net.net_sn_pc} />}
                  {net.net_sn_sc && <Info label={de ? "SN Scanner" : "SN scanner"} value={net.net_sn_sc} />}
                  {net.net_6lc_alt && <Info label={de ? "Alter Code" : "Old code"} value={net.net_6lc_alt} />}
                </div>
                {net.net_it_comment && <div className="mt-3 text-sm"><span className="text-[11px] font-mono uppercase tracking-wider text-gray-600">{de ? "IT-Kommentar" : "IT comment"}: </span><span className="text-gray-800">{net.net_it_comment}</span></div>}
                {net.net_tsr_remarks && <div className="mt-1 text-sm"><span className="text-[11px] font-mono uppercase tracking-wider text-gray-600">TSR: </span><span className="text-gray-800">{net.net_tsr_remarks}</span></div>}
                {net.net_source && <div className="mt-2 text-[10px] text-gray-600 font-mono">{de ? "Quelle" : "Source"}: {net.net_source}</div>}
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-gray-200">
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80 mb-3">{de ? "Geräte an diesem Standort" : "Devices at this location"} ({devices.length})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {devices.map((d) => (
                  <button key={d.device_code} data-testid={`loc-detail-dev-${d.device_code}`} onClick={() => navigate(`/d/${encodeURIComponent(d.device_code)}`)} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-[#c00000]/40 hover:bg-white/[0.07] transition-colors text-left">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[d.status] || "bg-gray-500"}`} />
                      <span className="font-mono text-sm text-gray-900 truncate">{d.device_code}</span>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                  </button>
                ))}
                {devices.length === 0 && <div className="text-gray-600 text-sm">{de ? "Keine Geräte." : "No devices."}</div>}
              </div>
            </div>
          </div>

          {/* Tickets / To-dos */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-amber-600/90"><Ticket className="w-4 h-4" /> Tickets / To-dos</div>
              <button data-testid="loc-ticket-new" onClick={() => { setEditing(null); setDlgOpen(true); }} className="flex items-center gap-1.5 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-2.5 py-1.5 text-xs transition-colors">
                <Plus className="w-3.5 h-3.5" /> {de ? "Neu" : "New"}
              </button>
            </div>

            <div className="space-y-2" data-testid="ticket-open-list">
              {openT.length === 0 && <div className="text-gray-600 text-sm py-2">{de ? "Keine offenen Tickets." : "No open tickets."}</div>}
              {openT.map((t) => <TicketCard key={t.id} ticket={t} users={users} onEdit={openEdit} onChanged={load} />)}
            </div>

            {closedT.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-2">{de ? "Erledigt" : "Done"} ({closedT.length})</div>
                <div className="space-y-2" data-testid="ticket-closed-list">
                  {closedT.map((t) => <TicketCard key={t.id} ticket={t} users={users} onEdit={openEdit} onChanged={load} />)}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
        <PortalLinkCard locationId={locationId} de={de} />
      </div>

      <TicketDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onSaved={load} initial={editing} lockLocation={locationId} devicesByLoc={devicesByLoc} users={users} />
    </div>
  );
}

function PortalLinkCard({ locationId, de }) {
  const { user } = useAuth();
  const canManage = can(user, "standorte", "edit");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/locations/${encodeURIComponent(locationId)}/portal-token`).then((r) => setToken(r.data?.token || "")).catch(() => {}); }, [locationId]);
  const url = token ? `${window.location.origin}/portal?token=${token}` : "";
  const gen = async () => { setBusy(true); try { const r = await api.post(`/locations/${encodeURIComponent(locationId)}/portal-token`); setToken(r.data.token); toast.success(de ? "Zugang erstellt" : "Access created"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const copy = () => { navigator.clipboard.writeText(url); toast.success(de ? "Link kopiert" : "Link copied"); };
  return (
    <div className="card-surface rounded-2xl p-5 mt-4" data-testid="portal-link-card">
      <div className="flex items-center gap-2 mb-1"><Link2 className="w-5 h-5 text-[#c00000]" /><h2 className="text-lg font-bold text-gray-900">{de ? "Kundenportal-Zugang" : "Customer portal access"}</h2></div>
      <p className="text-sm text-gray-600 mb-3">{de ? "Link/QR für diese Station zum selbstständigen Melden & Bestellen (ohne Login)." : "Link/QR for this station to self-serve (no login)."}</p>
      {token ? (
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="bg-white p-2 rounded-lg shrink-0"><QRCodeCanvas data-testid="portal-qr" value={url} size={116} /></div>
          <div className="min-w-0 flex-1 w-full">
            <div data-testid="portal-link-url" className="font-mono text-xs text-[#c00000] break-all bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{url}</div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button data-testid="portal-copy-btn" onClick={copy} className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-100"><Copy className="w-3.5 h-3.5" /> {de ? "Link kopieren" : "Copy link"}</button>
              <a data-testid="portal-open-btn" href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-100"><ExternalLink className="w-3.5 h-3.5" /> {de ? "Öffnen" : "Open"}</a>
              {canManage && <button data-testid="portal-regen-btn" onClick={gen} disabled={busy} className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 text-amber-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 disabled:opacity-50"><RefreshCw className="w-3.5 h-3.5" /> {de ? "Neu erzeugen" : "Regenerate"}</button>}
            </div>
            {canManage && <p className="text-[11px] text-gray-600 mt-2">{de ? "Neu erzeugen macht den alten Link ungültig." : "Regenerate invalidates the old link."}</p>}
          </div>
        </div>
      ) : canManage ? (
        <button data-testid="portal-gen-btn" onClick={gen} disabled={busy} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-3 py-2 text-sm disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />} {de ? "Portal-Zugang erstellen" : "Create portal access"}</button>
      ) : <p className="text-sm text-gray-600">{de ? "Kein Zugang eingerichtet." : "No access set up."}</p>}
    </div>
  );
}

const Info = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{label}</div>
    <div className="text-gray-800 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-gray-600" />}{value}</div>
  </div>
);

const InfoLink = ({ label, value, href, icon: Icon, testid }) => (
  <div>
    <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{label}</div>
    <a data-testid={testid} href={href} className="text-[#c00000] hover:text-[#c00000] hover:underline flex items-center gap-1.5 break-all">
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}{value}
    </a>
  </div>
);

const NetBool = ({ label, value }) => {
  const yes = String(value).toLowerCase() === "ja";
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{label}</div>
      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${yes ? "text-[#c00000]" : "text-red-700"}`}>
        <span className={`w-2 h-2 rounded-full ${yes ? "bg-[#c00000]" : "bg-red-400"}`} />{value}
      </span>
    </div>
  );
};
